import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, FileSpreadsheet, Trash2, Upload } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { api, selectedCompanyId } from "../lib/api";
import { cn, formatNumber } from "../lib/utils";

export const Route = createFileRoute("/forecasting/consumption")({
  component: ForecastConsumptionPage,
});

type ForecastTimezone = "CET" | "EET";

const MONTH_OPTIONS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

interface ConsumptionProfile {
  id: string;
  name: string;
  timezone: ForecastTimezone;
  unit: "MWh";
  month: string;
  intervalMinutes: number;
  rowCount: number;
  totalMwh: number;
  averageMw: number;
  peakMwh: number;
  sourceFilename: string | null;
  sourceSheet: string | null;
  updatedAt: string;
}

function ForecastConsumptionPage() {
  const queryClient = useQueryClient();
  const [month, setMonth] = useState(() => defaultTemplateMonth());
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const profilesQuery = useQuery({
    queryKey: ["forecasting", "consumption-profiles"],
    queryFn: () => api<{ profiles: ConsumptionProfile[] }>("/forecasting/consumption-profiles"),
  });

  const uploadProfile = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      const activeCompanyId = selectedCompanyId();
      const res = await fetch("/api/forecasting/consumption-profiles/upload", {
        method: "POST",
        credentials: "include",
        headers: activeCompanyId ? { "x-company-id": activeCompanyId } : undefined,
        body: fd,
      });
      const body = (await res.json()) as {
        profile?: ConsumptionProfile;
        warnings?: string[];
        error?: string;
      };
      if (!res.ok || !body.profile) throw new Error(body.error ?? res.statusText);
      return { profile: body.profile, warnings: body.warnings ?? [] };
    },
    onSuccess: () => {
      setUploadError(null);
      void queryClient.invalidateQueries({ queryKey: ["forecasting", "consumption-profiles"] });
    },
  });

  const deleteProfile = useMutation({
    mutationFn: (id: string) => api<{ ok: boolean }>(`/forecasting/consumption-profiles/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["forecasting", "consumption-profiles"] });
    },
  });

  const profiles = profilesQuery.data?.profiles ?? [];
  const summary = consumptionSummary(profiles);
  const error =
    errorMessage(profilesQuery.error) ||
    errorMessage(uploadProfile.error) ||
    errorMessage(deleteProfile.error) ||
    downloadError ||
    uploadError;

  function submitUpload(file: File) {
    setDragOver(false);
    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      setUploadError(`Unsupported file type: ${file.name}`);
      return;
    }
    setUploadError(null);
    uploadProfile.mutate(file);
  }

  async function downloadTemplate() {
    setDownloadError(null);
    const params = new URLSearchParams({ month });
    const activeCompanyId = selectedCompanyId();
    const res = await fetch(`/api/forecasting/consumption-profiles/template.xlsx?${params.toString()}`, {
      credentials: "include",
      headers: activeCompanyId ? { "x-company-id": activeCompanyId } : undefined,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      setDownloadError(body?.error ?? res.statusText);
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `consumption-profile-template-${month.replace("-", "")}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-base font-semibold">Consumption</h1>
          <div className="text-xs text-muted">Monthly customer curves from Excel profiles</div>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Template month">
            <MonthDropdown value={month} onChange={setMonth} />
          </Field>
          <button
            type="button"
            onClick={() => void downloadTemplate()}
            className="inline-flex items-center gap-2 rounded border border-border px-3 py-1.5 text-sm hover:border-accent"
          >
            <Download className="h-4 w-4" />
            Template
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            onDragEnter={(e) => {
              e.preventDefault();
              if (!uploadProfile.isPending) setDragOver(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (!uploadProfile.isPending) setDragOver(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              const nextTarget = e.relatedTarget;
              if (nextTarget instanceof Node && e.currentTarget.contains(nextTarget)) return;
              setDragOver(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              const file = e.dataTransfer.files[0];
              if (file && !uploadProfile.isPending) submitUpload(file);
            }}
            disabled={uploadProfile.isPending}
            className={cn(
              "inline-flex min-w-[7rem] items-center justify-center gap-2 rounded border px-3 py-1.5 text-sm transition-colors disabled:opacity-40",
              dragOver ? "border-dashed border-accent bg-accent/10 text-accent" : "border-accent bg-accent text-bg"
            )}
            title="Upload Excel workbook"
          >
            <Upload className="h-4 w-4" />
            {uploadProfile.isPending ? "Uploading" : dragOver ? "Drop here" : "Upload"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) submitUpload(file);
              e.target.value = "";
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Profiles" value={String(profiles.length)} detail={`${summary.monthCount} months`} />
        <Stat label="Total MWh" value={formatNumber(summary.totalMwh, 1)} detail="all uploaded curves" />
        <Stat label="Average MW" value={formatNumber(summary.averageMw, 3)} detail="weighted by rows" />
        <Stat label="Peak MWh" value={formatNumber(summary.peakMwh, 3)} detail="max hourly interval" />
      </div>

      {profilesQuery.isLoading && <div className="text-sm text-muted">Loading...</div>}
      {error && <div className="text-sm text-negRed">{error}</div>}
      {uploadProfile.data && (
        <div className="rounded-md border border-accent/40 bg-accent/10 p-3 text-sm">
          <div className="font-medium text-fg">{uploadProfile.data.profile.name}</div>
          <div className="mt-1 text-xs text-muted">
            {uploadProfile.data.profile.rowCount} rows · {formatNumber(uploadProfile.data.profile.totalMwh, 2)} MWh
          </div>
          {uploadProfile.data.warnings.length > 0 && (
            <div className="mt-1 text-xs text-muted">{uploadProfile.data.warnings.length} label warnings</div>
          )}
        </div>
      )}

      <section className="rounded-md border border-border/60">
        <div className="flex items-center gap-2 border-b border-border/60 px-3 py-2 text-xs font-medium">
          <FileSpreadsheet className="h-3.5 w-3.5" />
          Monthly curves
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[940px] text-xs">
            <thead className="bg-border/20 text-muted">
              <tr>
                <Th>Name</Th>
                <Th>Month</Th>
                <Th>Source</Th>
                <Th align="right">Rows</Th>
                <Th align="right">Total MWh</Th>
                <Th align="right">Average MW</Th>
                <Th align="right">Peak MWh</Th>
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {profiles.map((profile) => (
                <tr key={profile.id} className="border-t border-border/50 hover:bg-border/20">
                  <Td>
                    <div className="font-medium text-fg">{profile.name}</div>
                    <div className="text-[11px] text-muted">{profile.timezone} · {profile.unit}</div>
                  </Td>
                  <Td>{profile.month}</Td>
                  <Td>
                    <div>{profile.sourceFilename ?? "Manual"}</div>
                    <div className="text-[11px] text-muted">{profile.sourceSheet ?? "Intervals"}</div>
                  </Td>
                  <Td align="right">{formatNumber(profile.rowCount, 0)}</Td>
                  <Td align="right">{formatNumber(profile.totalMwh, 2)}</Td>
                  <Td align="right">{formatNumber(profile.averageMw, 3)}</Td>
                  <Td align="right">{formatNumber(profile.peakMwh, 3)}</Td>
                  <Td align="right">
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm("Delete this uploaded profile?")) deleteProfile.mutate(profile.id);
                      }}
                      disabled={deleteProfile.isPending && deleteProfile.variables === profile.id}
                      className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-negRed hover:border-negRed disabled:opacity-40"
                      title="Delete uploaded profile"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete
                    </button>
                  </Td>
                </tr>
              ))}
              {profiles.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-muted">
                    No monthly consumption profiles uploaded.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function consumptionSummary(profiles: ConsumptionProfile[]) {
  const rowCount = profiles.reduce((sum, profile) => sum + profile.rowCount, 0);
  const totalMwh = profiles.reduce((sum, profile) => sum + profile.totalMwh, 0);
  return {
    monthCount: new Set(profiles.map((profile) => profile.month)).size,
    totalMwh,
    averageMw: rowCount > 0 ? totalMwh / rowCount : 0,
    peakMwh: profiles.reduce((max, profile) => Math.max(max, profile.peakMwh), 0),
  };
}

function defaultTemplateMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-xs text-muted">
      {label}
      {children}
    </label>
  );
}

function MonthDropdown({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const [yearRaw, monthRaw] = value.split("-");
  const currentYear = new Date().getFullYear();
  const selectedYear = Number(yearRaw) || currentYear;
  const selectedMonth = Number(monthRaw) || new Date().getMonth() + 1;
  const years = useMemo(() => monthYearOptions(selectedYear), [selectedYear]);

  const updateMonth = (nextMonth: number) => onChange(formatMonthValue(selectedYear, nextMonth));
  const updateYear = (nextYear: number) => onChange(formatMonthValue(nextYear, selectedMonth));

  return (
    <div className="inline-flex gap-2">
      <select
        aria-label="Template month"
        value={selectedMonth}
        onChange={(e) => updateMonth(Number(e.target.value))}
        className="rounded border border-border bg-bg px-2 py-1.5 text-sm text-fg outline-none focus:border-accent"
      >
        {MONTH_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <select
        aria-label="Template year"
        value={selectedYear}
        onChange={(e) => updateYear(Number(e.target.value))}
        className="rounded border border-border bg-bg px-2 py-1.5 text-sm text-fg outline-none focus:border-accent"
      >
        {years.map((year) => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </select>
    </div>
  );
}

function monthYearOptions(selectedYear: number): number[] {
  const currentYear = new Date().getFullYear();
  const start = Math.min(selectedYear, currentYear - 4);
  const end = Math.max(selectedYear, currentYear + 1);
  const years = [];
  for (let year = end; year >= start; year--) years.push(year);
  return years;
}

function formatMonthValue(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, "0")}`;
}

function Stat({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="rounded-md border border-border/60 p-3">
      <div className="text-xs text-muted">{label}</div>
      <div className="mt-1 font-mono text-lg text-fg">{value}</div>
      <div className="mt-1 text-[11px] text-muted">{detail}</div>
    </div>
  );
}

function Th({ children, align = "left" }: { children: React.ReactNode; align?: "left" | "right" }) {
  return <th className={cn("px-3 py-2 font-medium", align === "right" ? "text-right" : "text-left")}>{children}</th>;
}

function Td({ children, align = "left" }: { children: React.ReactNode; align?: "left" | "right" }) {
  return <td className={cn("px-3 py-2 align-middle", align === "right" ? "text-right" : "text-left")}>{children}</td>;
}

function errorMessage(error: unknown): string | null {
  if (!error) return null;
  return error instanceof Error ? error.message : String(error);
}
